/**
 * Auto-generated from @intelishs-group/schema-core v5.3.18
 * Source: recursoshumanosTypes — Recursos Humanos — VacacionesController (solicitudes, saldos, pendientes por rol, calendario, dashboard)
 * Generated: 2026-10-28
 * Do not edit manually. Run: npm run generate:services
 */
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ApiBaseService } from './api-base.service';
import type {
  AprobarRechazarRequest,
  CreateSolicitudVacacionesRequest,
  DirectorAsignacionResponseListResponseDto,
  EditarSolicitudAdminRequest,
  EmpleadoEquipoSaldoResponseListResponseDto,
  SaldoVacacionesResponseResponseDto,
  SolicitudVacacionesResponseListResponseDto,
  SolicitudVacacionesResponseResponseDto,
  UpdateSolicitudVacacionesRequest,
  VacacionesCalendarioResponseListResponseDto,
  VacacionesDashboardResponseResponseDto,
} from '@schemas/rh-vacaciones';

@Injectable({ providedIn: 'root' })
export class RhVacacionesService extends ApiBaseService {
  private baseUrl = `${environment.BaseUrl}/Vacaciones`;

  /** Listado completo de solicitudes — solo Admin/RH. */
  async getVacaciones() {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}`);
  }

  /** Crear solicitud. Empleado normal solo puede crear para sí mismo; Admin/RH pueden crear para cualquier empleado (alta administrativa). Genera una fila por día hábil dentro del rango solicitado. */
  async createVacaciones(body: CreateSolicitudVacacionesRequest) {
    return this.post<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}`, body);
  }

  /** Detalle de una solicitud. El empleado solo puede ver las suyas; Admin/RH/Director ven cualquiera. */
  async getVacacionesById(id: number) {
    return this.get<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}`);
  }

  /** Editar fechas/comentarios de una solicitud Pendiente. El empleado solo puede editar las suyas; Admin/RH cualquiera. */
  async updateVacacionesById(id: number, body: UpdateSolicitudVacacionesRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}`, body);
  }

  /** Solicitudes de un empleado. Empleado normal solo puede consultar las suyas. */
  async empleado(empleadoId: number) {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}/empleado/${empleadoId}`);
  }

  /** Pendientes globales del Director — Admin/RH/Director. */
  async pendientes_director() {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}/pendientes/director`);
  }

  /** Solicitudes pendientes filtradas por áreas del director. Excluye al director solicitante de su propio listado (no auto-aprobación). TODO: cuando los claims expongan las áreas asignadas al director, derivar `areas` del JWT y eliminar el parámetro de query. */
  async pendientes_misAreas(areas: string[]) {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}/pendientes/mis-areas`, {
      areas: areas,
    });
  }

  /** Solicitudes pendientes de directores de área (para el Director General). Excluye al DG de su propio listado (no auto-aprobación). */
  async pendientes_dg(ids: number[]) {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}/pendientes/dg`, {
      ids: ids,
    });
  }

  /** Pendientes para revisión final de RH — Admin/RH. */
  async pendientes_rh() {
    return this.get<SolicitudVacacionesResponseListResponseDto>(`${this.baseUrl}/pendientes/rh`);
  }

  /** Cancelar solicitud. Solo el dueño o Admin/RH. */
  async cancelar(id: number) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/cancelar`, null);
  }

  /** Paso 1 del flujo: el Director aprueba la solicitud (Pendiente → Aprobada Director). El aprobador se persiste en `fkidempleadosuperiorautoriza`. */
  async aprobarDirector(id: number, body: AprobarRechazarRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/aprobar-director`, body);
  }

  /** Paso 1 del flujo: el Director rechaza la solicitud (Pendiente → Rechazada Director). */
  async rechazarDirector(id: number, body: AprobarRechazarRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/rechazar-director`, body);
  }

  /** Paso 2 del flujo: RH da la aprobación final (Aprobada Director → Aprobada). El aprobador se persiste en `fkidempleadorhautoriza`. También acepta una solicitud Pendiente: RH decide sin esperar al Director y el paso del Director queda vacío. */
  async aprobarRh(id: number, body: AprobarRechazarRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/aprobar-rh`, body);
  }

  /** Paso 2 del flujo: RH rechaza la solicitud (Aprobada Director → Rechazada). También acepta una solicitud Pendiente. */
  async rechazarRh(id: number, body: AprobarRechazarRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/rechazar-rh`, body);
  }

  /** Edición administrativa libre de una solicitud (fechas, estatus, aprobadores) — solo Admin/RH. Soporta <c>AprobadorDirectorTipo / AprobadorRHTipo = "yo"</c>, que se resuelve al usuario del JWT. */
  async admin(id: number, body: EditarSolicitudAdminRequest) {
    return this.put<SolicitudVacacionesResponseResponseDto>(`${this.baseUrl}/${id}/admin`, body);
  }

  /** Saldo de vacaciones de un empleado (cálculo FIFO directo desde `rh.movempleadovacacion`). Solo el propio o Admin/RH/Director. */
  async saldo(empleadoId: number, excluirSolicitudId: number) {
    return this.get<SaldoVacacionesResponseResponseDto>(`${this.baseUrl}/saldo/${empleadoId}`, {
      excluirSolicitudId: excluirSolicitudId.toString(),
    });
  }

  /** Dashboard ejecutivo — Admin/RH. */
  async dashboard() {
    return this.get<VacacionesDashboardResponseResponseDto>(`${this.baseUrl}/dashboard`);
  }

  /** Calendario de vacaciones aprobadas/parcialmente-aprobadas en el rango — Admin/RH/Director. Para un Director (sin rol Admin/RH) se restringe automáticamente a las áreas a las que pertenece. */
  async calendario(desde: string, hasta: string) {
    return this.get<VacacionesCalendarioResponseListResponseDto>(`${this.baseUrl}/calendario`, {
      desde: desde,
      hasta: hasta,
    });
  }

  /** Vista de equipo del director — Admin/RH/Director. Saldo, pendientes y próximas vacaciones de cada miembro. Filtrar por áreas (director de área) o por IDs de empleados (DG con sus directores supervisados); si ambos filtros vienen vacíos devuelve lista vacía. */
  async equipoSaldo(area: string[], empleadoId: number[]) {
    return this.get<EmpleadoEquipoSaldoResponseListResponseDto>(`${this.baseUrl}/equipo-saldo`, {
      area: area,
      empleadoId: empleadoId,
    });
  }

  /** Directores y sus áreas asignadas — Admin/RH/Director. */
  async directores() {
    return this.get<DirectorAsignacionResponseListResponseDto>(`${this.baseUrl}/directores`);
  }
}
